import { useContext, useEffect, useRef } from 'react';
import { UNSAFE_NavigationContext as NavigationContext } from 'react-router-dom';

/**
 * Custom hook to block navigation when there are unsaved changes.
 * Works with legacy BrowserRouter (non-data router).
 *
 * @param {boolean} shouldBlock - Whether navigation should be blocked
 * @param {function} onBlock - Callback when navigation is blocked, receives { proceed, cancel }
 */
export function useNavigationBlocker(shouldBlock, onBlock) {
  const { navigator } = useContext(NavigationContext);

  // Use refs to always have access to current values in closures
  const shouldBlockRef = useRef(shouldBlock);
  const onBlockRef = useRef(onBlock);

  // Keep refs updated
  useEffect(() => {
    shouldBlockRef.current = shouldBlock;
    onBlockRef.current = onBlock;
  }, [shouldBlock, onBlock]);

  useEffect(() => {
    // Store original push and replace methods
    const originalPush = navigator.push;
    const originalReplace = navigator.replace;

    // Override push method
    navigator.push = (...args) => {
      if (!shouldBlockRef.current) {
        originalPush.apply(navigator, args);
        return;
      }

      onBlockRef.current({
        proceed: () => {
          navigator.push = originalPush;
          navigator.replace = originalReplace;
          originalPush.apply(navigator, args);
        },
        cancel: () => {
          // Do nothing, stay on current page
        },
      });
    };

    // Override replace method
    navigator.replace = (...args) => {
      if (!shouldBlockRef.current) {
        originalReplace.apply(navigator, args);
        return;
      }

      onBlockRef.current({
        proceed: () => {
          navigator.push = originalPush;
          navigator.replace = originalReplace;
          originalReplace.apply(navigator, args);
        },
        cancel: () => {
          // Do nothing, stay on current page
        },
      });
    };

    // Handle browser back/forward buttons
    const handlePopState = () => {
      if (!shouldBlockRef.current) {
        return;
      }

      // Push current state back to prevent navigation
      window.history.pushState(null, '', window.location.href);

      onBlockRef.current({
        proceed: () => {
          navigator.push = originalPush;
          navigator.replace = originalReplace;
          window.history.back();
        },
        cancel: () => {
          // Already prevented by pushState above
        },
      });
    };

    window.addEventListener('popstate', handlePopState);

    // Cleanup
    return () => {
      navigator.push = originalPush;
      navigator.replace = originalReplace;
      window.removeEventListener('popstate', handlePopState);
    };
  }, [navigator]);

  // Handle beforeunload (browser refresh/close)
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (!shouldBlockRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);
}